import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ThemeContext = createContext(null);

// Text-size steps applied site-wide via CSS zoom (100% = default)
export const FONT_SCALE_STEPS = [100, 112, 125, 140];

export function ThemeProvider({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('prot-theme') || 'dark'; }
    catch { return 'dark'; }
  });

  const [fontScale, setFontScale] = useState(() => {
    try {
      const saved = Number(localStorage.getItem('prot-font-scale'));
      return FONT_SCALE_STEPS.includes(saved) ? saved : FONT_SCALE_STEPS[0];
    } catch { return FONT_SCALE_STEPS[0]; }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('prot-theme', theme); } catch {}
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.zoom = `${fontScale}%`;
    try { localStorage.setItem('prot-font-scale', String(fontScale)); } catch {}
  }, [fontScale]);

  const toggleMenu  = useCallback(() => setMenuOpen(p => !p), []);
  const closeMenu   = useCallback(() => setMenuOpen(false),   []);
  const toggleTheme = useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), []);

  const increaseFontScale = useCallback(() => {
    setFontScale(s => {
      const i = FONT_SCALE_STEPS.indexOf(s);
      return FONT_SCALE_STEPS[Math.min(i + 1, FONT_SCALE_STEPS.length - 1)];
    });
  }, []);

  const decreaseFontScale = useCallback(() => {
    setFontScale(s => {
      const i = FONT_SCALE_STEPS.indexOf(s);
      return FONT_SCALE_STEPS[Math.max(i - 1, 0)];
    });
  }, []);

  return (
    <ThemeContext.Provider value={{
      menuOpen, toggleMenu, closeMenu,
      theme, toggleTheme,
      fontScale, increaseFontScale, decreaseFontScale,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
